import type { ValueFormatterParams } from 'ag-grid-community';
import appConstants from '@appConstants';
import { MultiSelectFilter, DateTimeFilter } from '@dataGrid/components/columnFilters';
import { mergeUserConfig } from '@dataGrid/gridUtilities';
import { TRAILER_SLOTS } from './constants';
import type { TrailerMoveColDef } from './types';

const childDetailValueFormatter = (params: ValueFormatterParams) => params.value ?? '';

export interface DetailGridConfigConfiguration {
  userProfile?: {
    columnPref?: Record<string, unknown>;
  };
}

export interface TrailerMovesDetailGridConfig {
  title: string;
  keyColumn: string;
  rowSelection: string;
  rowDeselection: boolean;
  defaultColDef: {
    sortable: boolean;
    filter: boolean;
    resizable: boolean;
    editable: boolean;
    suppressSpanHeaderHeight: boolean;
  };
  columns: TrailerMoveColDef[];
}

export default class DetailGridConfig {
  name: string;
  config: TrailerMovesDetailGridConfig;

  constructor(configuration?: DetailGridConfigConfiguration) {
    const config = configuration || {};
    this.name = 'GridConfiguration';
    this.config = DetailGridConfig.getConfiguration(config);
  }

  static getConfiguration({
    userProfile,
  }: DetailGridConfigConfiguration = {}): TrailerMovesDetailGridConfig {
    const defaultConfig = DetailGridConfig.getDefaultConfiguration(userProfile);
    const userConfig = userProfile?.columnPref;
    return mergeUserConfig({
      defaultConfig,
      userConfig,
    }) as TrailerMovesDetailGridConfig;
  }

  static getDefaultConfiguration(
    _userProfile?: DetailGridConfigConfiguration['userProfile']
  ): TrailerMovesDetailGridConfig {
    return {
      title: 'TrailerMovesDetailGrid',
      keyColumn: 'pickupId',
      rowSelection: 'single',
      rowDeselection: true,
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        editable: false,
        suppressSpanHeaderHeight: true,
      },
      columns: [
        {
          headerName: 'Move Type',
          headerTooltip: 'Move Type',
          field: 'moveType',
          minWidth: appConstants.columnWidth.W_120,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          valueFormatter: childDetailValueFormatter,
        },
        {
          headerName: 'Driver',
          headerTooltip: 'Driver Name',
          field: 'driverName',
          minWidth: appConstants.columnWidth.DISPATCH_DRIVER_NAME,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          valueFormatter: childDetailValueFormatter,
        },
        {
          headerName: 'Trailer 1',
          headerTooltip: 'Trailer 1',
          field: TRAILER_SLOTS.TRAILER_1,
          minWidth: appConstants.columnWidth.W_120,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          valueFormatter: childDetailValueFormatter,
        },
        {
          headerName: 'Dolly#',
          headerTooltip: 'Dolly Number',
          field: 'dollyNumber',
          minWidth: appConstants.columnWidth.W_100,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          valueFormatter: childDetailValueFormatter,
        },
        {
          headerName: 'Trailer 2',
          headerTooltip: 'Trailer 2',
          field: TRAILER_SLOTS.TRAILER_2,
          minWidth: appConstants.columnWidth.W_120,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          valueFormatter: childDetailValueFormatter,
        },
        {
          headerName: 'Driver Number',
          headerTooltip: 'Driver Number',
          field: 'driverNumber',
          minWidth: appConstants.columnWidth.DRIVER_NUMBER,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          filterParams: {
            filterType: appConstants.FILTER_TYPES.STRING,
          },
          valueFormatter: childDetailValueFormatter,
        },
        {
          headerName: 'Ready Time',
          headerTooltip: 'Ready Time',
          field: 'readyTime',
          minWidth: appConstants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          valueFormatter: childDetailValueFormatter,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: appConstants.DATETYPES.TIME,
            isTimeInterval: true,
          },
        },
        {
          headerName: 'Close Time',
          headerTooltip: 'Close Time',
          field: 'closeTime',
          minWidth: appConstants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          valueFormatter: childDetailValueFormatter,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: appConstants.DATETYPES.TIME,
            isTimeInterval: true,
          },
        },
      ],
    };
  }
}
